import { useMemo, useState } from "react"
import Select from "react-select"
import { Button, Form, FormGroup, Input, Label, Spinner } from "reactstrap"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"
import FormikCreatableField from "components/Common/FormikCreatableField"
import LabelButtons from "components/Common/LabelButtons"
import PublishDraftSwitch from "components/Common/PublishDraftSwitch/PublishDraftSwitch"
import ThemeModal from "components/Common/ThemeModal"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useBulkUpdateStockStatusPublish } from "hooks/stockHook"
import { useToastMessage } from "hooks/useToastMessage"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"

const downloadMissingChassisList = missingChassisList => {
  if (!missingChassisList || missingChassisList.length === 0) return

  const now = new Date()
  const timestamp = now
    .toISOString()
    .replace(/[:.]/g, "-")
  const content = missingChassisList.join("\n")
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = url
  link.download = `missing-chassis-${timestamp}.txt`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const BulkStatusPublishModal = ({
  onUpdated = () => {},
  btnProps = {},
  renderTrigger,
}) => {
  const { handleResponse, handleError } = useToastMessage()
  const { hasPermission } = useAuth()
  const canDetachOtherReadyStocks = hasPermission(
    ACCESS.STOCK.DETACH_READY_STOCK_FROM_OTHER_VEHICLES
  )
  const canEditStock = hasPermission(ACCESS.STOCK.EDIT)
  const canTagImporter =
    canEditStock && hasPermission(ACCESS.STOCK.EDIT_IMPORTER)
  const [isOpen, setIsOpen] = useState(false)
  const [chassisList, setChassisList] = useState("")
  const [statusId, setStatusId] = useState("")
  const [importerId, setImporterId] = useState("")
  const [importerTouched, setImporterTouched] = useState(false)
  const [detachImporterReadyStocks, setDetachImporterReadyStocks] =
    useState(false)
  const [isPublishedTouched, setIsPublishedTouched] = useState(false)
  const [isPublished, setIsPublished] = useState(false)
  const [readyStockTouched, setReadyStockTouched] = useState(false)
  const [isReadyStock, setIsReadyStock] = useState(false)
  const [detachOtherReadyStocks, setDetachOtherReadyStocks] = useState(false)
  const [platformsTouched, setPlatformsTouched] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState([])

  const { isLoading: optionsLoading, raw } = useFilteredVehicleOptions({
    isAll: true,
  })

  const { mutateAsync: bulkUpdate, isPending } =
    useBulkUpdateStockStatusPublish()

  const statusFormik = useMemo(
    () => ({
      values: { vs_statuse_id: statusId },
      errors: {},
      touched: {},
      setTouched: () => {},
      setFieldValue: (name, value) => {
        if (name === "vs_statuse_id") {
          setStatusId(value)
        }
      },
    }),
    [statusId]
  )

  const importerOptions = useMemo(
    () =>
      (raw && raw.importers ? raw.importers : []).map(importer => ({
        value: importer && importer.id ? importer.id.toString() : "",
        label: importer && importer.name ? importer.name : "",
      })),
    [raw]
  )

  const selectedImporter = useMemo(
    () =>
      importerOptions.find(
        option => option.value === importerId.toString()
      ) || null,
    [importerId, importerOptions]
  )

  const toggle = () => setIsOpen(prev => !prev)

  const resetForm = () => {
    setChassisList("")
    setStatusId("")
    setImporterId("")
    setImporterTouched(false)
    setDetachImporterReadyStocks(false)
    setIsPublishedTouched(false)
    setIsPublished(false)
    setReadyStockTouched(false)
    setIsReadyStock(false)
    setDetachOtherReadyStocks(false)
    setPlatformsTouched(false)
    setSelectedPlatforms([])
  }

  const handlePublishedToggle = value => {
    setIsPublishedTouched(true)
    setIsPublished(value)
  }

  const handlePlatformsChange = newSelected => {
    setPlatformsTouched(true)
    setSelectedPlatforms(newSelected)
  }

  const handleReadyStockToggle = value => {
    setReadyStockTouched(true)
    setIsReadyStock(value)
    if (!value) {
      setDetachOtherReadyStocks(false)
    }
  }

  const handleImporterChange = selected => {
    setImporterTouched(true)
    setImporterId(selected ? selected.value : "")
    if (!selected) {
      setDetachImporterReadyStocks(false)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const full_chassis_list = chassisList
      .split(/\r?\n/)
      .map(value => value.trim())
      .filter(Boolean)

    if (full_chassis_list.length === 0) {
      handleResponse(
        {
          success: false,
          message: "Please enter at least one chassis number.",
        },
        "",
        true
      )
      return
    }

    const payload = { full_chassis_list }

    if (statusId !== "") {
      payload.vs_statuse_id = statusId
    }

    if (isPublishedTouched) {
      payload.is_published = isPublished
    }

    if (readyStockTouched) {
      payload.vs_ready_stock = isReadyStock

      if (
        isReadyStock &&
        detachOtherReadyStocks &&
        canDetachOtherReadyStocks
      ) {
        payload.detach_other_ready_stocks = true
      }
    }

    if (platformsTouched) {
      payload.platformIds = selectedPlatforms.map(Number)
    }

    if (canTagImporter && importerTouched && importerId !== "") {
      payload.importer_id = Number(importerId)
    }

    if (
      canTagImporter &&
      canDetachOtherReadyStocks &&
      detachImporterReadyStocks &&
      importerId !== ""
    ) {
      payload.detach_importer_ready_stocks = true
    }

    if (
      !Object.prototype.hasOwnProperty.call(payload, "vs_statuse_id") &&
      !Object.prototype.hasOwnProperty.call(payload, "is_published") &&
      !Object.prototype.hasOwnProperty.call(payload, "vs_ready_stock") &&
      !Object.prototype.hasOwnProperty.call(payload, "platformIds") &&
      !Object.prototype.hasOwnProperty.call(payload, "importer_id") &&
      !Object.prototype.hasOwnProperty.call(
        payload,
        "detach_importer_ready_stocks"
      )
    ) {
      handleResponse(
        {
          success: false,
          message:
            "Select at least one value to update: status, importer, importer ready stock detach, Draft & Publish, Ready Stock, or publishable platform.",
        },
        "",
        true
      )
      return
    }

    try {
      const res = await bulkUpdate(payload)
      const missingCount = res?.data?.missing_chassis ?? 0
      const missingChassisList = res?.data?.missing_chassis_list ?? []
      const message = missingCount
        ? `${res?.message ?? "Bulk stock update completed."} ${missingCount} chassis not found.`
        : res?.message ?? "Bulk stock update completed."

      handleResponse(res, message, true)

      if (res?.success && missingChassisList.length > 0) {
        downloadMissingChassisList(missingChassisList)
      }

      if (res?.success) {
        resetForm()
        setIsOpen(false)
        onUpdated()
      }
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ toggle })
      ) : (
        <Button color="warning" onClick={toggle} {...btnProps}>
          <i className="bx bx-edit-alt me-1" />
          Bulk Status
        </Button>
      )}

      <ThemeModal
        isOpen={isOpen}
        toggle={toggle}
        title="Bulk Status & Publish Update"
        size="lg"
      >
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="bulkStatusChassisList">
              Chassis Numbers{" "}
              <small className="text-muted">(one per line)</small>
            </Label>
            <Input
              type="textarea"
              id="bulkStatusChassisList"
              rows="8"
              placeholder={`Example:
NKE165-7251102
MXPJ10-2023538
NRE161-0090201
ZWR80-0482845`}
              value={chassisList}
              onChange={e => setChassisList(e.target.value)}
            />
          </FormGroup>

          <FormikCreatableField
            name="vs_statuse_id"
            label="Status"
            formik={statusFormik}
            data={raw?.statuses ?? []}
            isLoading={optionsLoading}
            placeholder="Select Status"
          />

          {canTagImporter && (
            <div className="card mb-3">
              <div className="card-header bg-light">
                <h6 className="mb-0">Importer Tagging</h6>
              </div>
              <div className="card-body">
                <Label for="bulkStatusImporter">Importer</Label>
                <Select
                  inputId="bulkStatusImporter"
                  isClearable
                  isLoading={optionsLoading}
                  options={importerOptions}
                  value={selectedImporter}
                  placeholder="Select Importer"
                  onChange={handleImporterChange}
                  styles={{
                    menu: provided => ({ ...provided, zIndex: 9999 }),
                  }}
                />

                {canDetachOtherReadyStocks && (
                  <div className="mt-3">
                    <Button
                      color={
                        detachImporterReadyStocks
                          ? "danger"
                          : "outline-danger"
                      }
                      type="button"
                      size="sm"
                      disabled={importerId === ""}
                      onClick={() =>
                        setDetachImporterReadyStocks(prev => !prev)
                      }
                    >
                      <i
                        className={`bx ${
                          detachImporterReadyStocks
                            ? "bx-check-circle"
                            : "bx-unlink"
                        } me-1`}
                      />
                      Detach Ready Stock from selected importer
                    </Button>
                    <div className="text-muted small mt-1">
                      {importerId === ""
                        ? "Select an importer to detach their Ready Stock vehicles."
                        : detachImporterReadyStocks
                        ? "Enabled: existing chassis above will stay Ready Stock; other Ready Stock vehicles for this importer will become Not Ready."
                        : "Click to make this importer's other Ready Stock vehicles Not Ready."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card mb-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">Vehicle Publishment</h6>
            </div>
            <div className="card-body">
              <LabelButtons name="Publishable Platform" />
              <ButtonCheckboxList
                items={raw?.social_platforms ?? []}
                selected={selectedPlatforms}
                onChange={handlePlatformsChange}
                maxVisibleItems={20}
              />
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">Draft & Publish</h6>
            </div>
            <div className="card-body">
              <PublishDraftSwitch
                initialValue={isPublished}
                onToggle={handlePublishedToggle}
              />
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">Ready Stock</h6>
            </div>
            <div className="card-body">
              <PublishDraftSwitch
                initialValue={isReadyStock}
                trueLabel="Ready Stock"
                falseLabel="Not Ready"
                trueIcon="bx-check-circle"
                falseIcon="bx-time-five"
                onToggle={handleReadyStockToggle}
              />

              {readyStockTouched && isReadyStock && canDetachOtherReadyStocks && (
                <div className="mt-3">
                  <Button
                    color={detachOtherReadyStocks ? "danger" : "outline-danger"}
                    type="button"
                    size="sm"
                    onClick={() =>
                      setDetachOtherReadyStocks(prev => !prev)
                    }
                  >
                    <i
                      className={`bx ${
                        detachOtherReadyStocks
                          ? "bx-check-circle"
                          : "bx-unlink"
                      } me-1`}
                    />
                    Detach Ready Stock from other vehicles
                  </Button>
                  <div className="text-muted small mt-1">
                    {detachOtherReadyStocks
                      ? "Enabled: only the chassis numbers above will stay Ready Stock."
                      : "Click to make every other ready stock vehicle Not Ready."}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="text-end">
            <Button
              color="light"
              type="button"
              className="me-2"
              onClick={toggle}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isPending}>
              {isPending ? <Spinner size="sm" /> : "Update Vehicles"}
            </Button>
          </div>
        </Form>
      </ThemeModal>
    </>
  )
}

export default BulkStatusPublishModal
